'use client'

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  CHIAVE_STORAGE,
  chiaveRiga,
  normalizzaRighe,
  QUANTITA_MAX,
  type RigaCarrello,
  unisciRighe,
} from './righe'

type CarrelloContext = {
  righe: RigaCarrello[]
  /** False until localStorage has been read (server render and first client render). */
  pronto: boolean
  /** Sum of the quantities. */
  articoli: number
  aggiungi: (riga: RigaCarrello) => void
  impostaQuantita: (chiave: string, quantita: number) => void
  rimuovi: (chiave: string) => void
  /** Replaces every line, e.g. with the server-validated ones. */
  sostituisci: (righe: RigaCarrello[]) => void
}

const Context = createContext<CarrelloContext | null>(null)

const leggi = (): RigaCarrello[] => {
  try {
    return normalizzaRighe(JSON.parse(localStorage.getItem(CHIAVE_STORAGE) || '[]')).righe
  } catch {
    return []
  }
}

/**
 * The cart, kept in localStorage under `vs-carrello`. Only SKUs, quantities and paints are
 * stored: prices are never trusted from here, `validaCarrello` re-reads them.
 */
export const CarrelloProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [righe, setRighe] = useState<RigaCarrello[]>([])
  const [pronto, setPronto] = useState(false)

  useEffect(() => {
    setRighe(leggi())
    setPronto(true)

    // Keeps several open tabs in sync.
    const onStorage = (e: StorageEvent) => {
      if (e.key === CHIAVE_STORAGE || e.key === null) setRighe(leggi())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const aggiorna = useCallback((fn: (righe: RigaCarrello[]) => RigaCarrello[]) => {
    setRighe((prev) => {
      const next = unisciRighe(fn(prev))
      try {
        localStorage.setItem(CHIAVE_STORAGE, JSON.stringify(next))
      } catch {
        // Private mode or full storage: the cart still works for this page view.
      }
      return next
    })
  }, [])

  const value = useMemo<CarrelloContext>(
    () => ({
      righe,
      pronto,
      articoli: righe.reduce((n, r) => n + r.quantita, 0),
      aggiungi: (riga) => aggiorna((prev) => [...prev, riga]),
      impostaQuantita: (chiave, quantita) =>
        aggiorna((prev) =>
          prev.map((r) =>
            chiaveRiga(r) === chiave
              ? { ...r, quantita: Math.max(1, Math.min(QUANTITA_MAX, Math.trunc(quantita) || 1)) }
              : r,
          ),
        ),
      rimuovi: (chiave) => aggiorna((prev) => prev.filter((r) => chiaveRiga(r) !== chiave)),
      sostituisci: (nuove) => aggiorna(() => nuove),
    }),
    [righe, pronto, aggiorna],
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const useCarrello = () => {
  const context = useContext(Context)
  if (!context) throw new Error('useCarrello va usato dentro <CarrelloProvider>')
  return context
}
